/* eslint-disable @typescript-eslint/no-unused-vars */
import { Moon, Sun } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import loginImg from '../../src/VESTN_FullLogo.png';
import { useTheme } from '../hooks/useTheme';
import { services } from '../services'; // your existing axios wrapper
import { web3Services } from '../services/web3.service';
import { APIError, AuthResponse, LoginPageProps } from '../types/login/login';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './ui/use-toast';

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [connectingWallet, setConnectingWallet] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired')) {
      sessionStorage.removeItem('sessionExpired');
      toast({
        title: 'Session expired',
        description: 'Your session has expired due to inactivity. Please sign in again.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    console.log('[Login] Submit started');

    try {
      const response = await services.post<AuthResponse>('/admin/login', {
        username: email, // Notice: your backend expects username
        password: password,
      });
      console.log('[Login] API response status:', response?.status, 'data:', response?.data);

      if (typeof response === 'string' || response instanceof Error) {
        throw new Error('Authentication failed');
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }

      const body = response?.data as AuthResponse | undefined;
      const payload: { token?: string; user?: unknown } | undefined =
        body && 'data' in body && body.data
          ? body.data
          : (body as { token?: string; user?: unknown });
      const token = payload?.token;
      const user = payload?.user;
      console.log('[Login] Payload extracted, hasToken:', !!token, 'hasUser:', !!user);

      if (!token || !user) {
        console.log('[Login] Missing token or user, showing error');
        setError('Invalid login response. Please try again.');
        setLoading(false);
        return;
      }

      // Save token and user details
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('[Login] Token and user saved, now requesting wallet connection...');

      // Show "Connect wallet..." and yield so UI updates before opening MetaMask
      setLoading(false);
      setConnectingWallet(true);
      setError('');
      await new Promise((r) => setTimeout(r, 100));

      const walletConnected = await web3Services.enableMetamask();
      console.log('[Login] enableMetamask returned:', walletConnected);

      if (!walletConnected) {
        console.log(
          '[Login] Wallet not connected, window.ethereum:',
          typeof (window as unknown as { ethereum?: unknown }).ethereum
        );
        setError(
          typeof window !== 'undefined' && (window as unknown as { ethereum?: unknown }).ethereum
            ? 'Wallet connection is required. Please connect your MetaMask wallet to continue.'
            : 'MetaMask not detected. Please install MetaMask and connect your wallet to continue.'
        );
        setConnectingWallet(false);
        return;
      }

      console.log('[Login] Wallet connected, navigating to dashboard');
      setConnectingWallet(false);
      onLogin();
      navigate('/dashboard');
    } catch (err) {
      console.log('[Login] Caught error:', err);
      const error = err as APIError;
      setError(error.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
      setConnectingWallet(false);
    }
  };

  return (
    <div className='min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden bg-white dark:bg-[#0B222C]'>
      {/* Header */}
      <div className='absolute top-0 left-0 right-0 z-20 p-3 sm:p-4'>
        <div className='flex justify-end'>
          <Button
            variant='ghost'
            size='icon'
            onClick={toggleTheme}
            className='bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 dark:bg-gray-800/10 dark:border-gray-700/20 dark:hover:bg-gray-700/20 transition-all duration-300 w-10 h-10 sm:w-11 sm:h-11'
          >
            {theme === 'dark' ? (
              <Sun className='w-4 h-4 sm:w-5 sm:h-5 text-yellow-400' />
            ) : (
              <Moon className='w-4 h-4 sm:w-5 sm:h-5 text-blue-600' />
            )}
          </Button>
        </div>
      </div>

      {/* Heading */}
      <div className='relative z-10 mb-8 text-center'>
        <h1 className='text-4xl md:text-5xl font-bold mb-2 text-gray-900 dark:text-[#E9E6C9]'>
          Welcome to VESTN RWA
        </h1>
        <p className='text-lg font-medium text-gray-700 dark:text-[#E9E6C9]'>Admin Panel</p>
      </div>

      {/* Card */}
      <div className='relative z-10 w-full max-w-5xl mx-4 sm:mx-auto'>
        <div className='flex flex-col lg:flex-row shadow-2xl rounded-2xl overflow-hidden backdrop-blur-sm bg-white/5 dark:bg-white/5 border-2 border-gray-200 dark:border-[#095D52]'>
          {/* Left side - Image */}
          <div className='hidden lg:block lg:w-1/2 relative'>
            <div className='h-full flex items-center justify-center p-6 sm:p-8 relative bg-gray-50 dark:bg-[#0B222C]'>
              {/* Decorative elements */}
              <div className='absolute top-4 right-4 w-16 h-16 sm:w-20 sm:h-20 bg-green-400/20 rounded-full blur-xl'></div>
              <div className='absolute bottom-4 left-4 w-12 h-12 sm:w-16 sm:h-16 bg-yellow-400/20 rounded-full blur-lg'></div>

              <div className='relative z-10'>
                <img
                  src={loginImg}
                  alt='Login Illustration'
                  className='object-contain max-h-64 sm:max-h-80 lg:max-h-96 w-auto drop-shadow-2xl'
                />
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className='w-full lg:w-1/2 p-6 sm:p-8 lg:p-12 relative bg-white dark:bg-[#0B222C] backdrop-blur-sm'>
            {/* Form header */}
            <div className='mb-8 text-center'>
              <div className='inline-flex items-center justify-center w-16 h-16 bg-green-400/20 rounded-full mb-4'>
                <svg
                  className='w-8 h-8 text-green-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                  />
                </svg>
              </div>
              <h2 className='text-2xl font-bold mb-2 text-gray-900 dark:text-[#E9E6C9]'>Sign in</h2>
              <p className='text-sm text-gray-600 dark:text-[#E9E6C9]'>
                Please enter your credentials to access the admin panel
              </p>
            </div>

            <form className='space-y-6' onSubmit={submit}>
              <div className='space-y-5'>
                <div className='space-y-2'>
                  <label
                    htmlFor='username'
                    className='text-sm font-semibold flex items-center gap-2 text-gray-700 dark:text-[#E9E6C9]'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
                      />
                    </svg>
                    Username
                  </label>
                  <Input
                    id='username'
                    name='username'
                    type='text'
                    autoComplete='username'
                    required
                    placeholder='Enter your username'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className='w-full transition-all duration-200 bg-white border-gray-300 text-gray-900 dark:bg-[#0b222c] dark:border-[#095D52] dark:text-[#e9e6c9] placeholder-gray-500 dark:placeholder-gray-400'
                  />
                </div>

                <div className='space-y-2'>
                  <label
                    htmlFor='password'
                    className='text-sm font-semibold flex items-center gap-2 text-gray-700 dark:text-[#E9E6C9]'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                      />
                    </svg>
                    Password
                  </label>
                  <div className='relative'>
                    <Input
                      id='password'
                      name='password'
                      type={showPassword ? 'text' : 'password'}
                      autoComplete='current-password'
                      required
                      placeholder='Enter your password'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className='w-full transition-all duration-200 pr-12 bg-white border-gray-300 text-gray-900 dark:bg-[#0b222c] dark:border-[#095D52] dark:text-[#e9e6c9] placeholder-gray-500 dark:placeholder-gray-400'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 dark:text-[#E9E6C9] dark:hover:text-[#E9E6C9]/80 transition-colors duration-200'
                    >
                      {showPassword ? (
                        <svg
                          className='w-5 h-5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
                          />
                        </svg>
                      ) : (
                        <svg
                          className='w-5 h-5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <div className='text-red-600 dark:text-red-300 text-sm text-center bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-500/30 p-3 rounded-lg backdrop-blur-sm'>
                  <div className='flex items-center justify-center gap-2'>
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                    {error}
                  </div>
                </div>
              )}

              <Button
                type='submit'
                className='w-full h-12 text-base font-semibold transition-all duration-300 transform hover:scale-[1.02] bg-gradient-to-r from-green-400 to-green-500 text-white dark:from-green-500 dark:to-green-600 dark:text-[#0B222C] shadow-lg hover:shadow-xl'
                disabled={loading || connectingWallet}
              >
                {loading ? (
                  <span className='flex items-center justify-center gap-2'>
                    <svg
                      className='animate-spin h-5 w-5'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    Signing in...
                  </span>
                ) : connectingWallet ? (
                  <span className='flex items-center justify-center gap-2'>
                    <svg
                      className='animate-spin h-5 w-5'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    Connect wallet...
                  </span>
                ) : (
                  <span className='flex items-center justify-center gap-2'>
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1'
                      />
                    </svg>
                    Sign in
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
